import { toRoman } from '../../../src/utils/tiptap/customListItemExtension/toRoman.js'
import { listItemConfigurations } from './customListItemExtension/listItemConfigurations.js'
import { formatCustomListItemSymbol } from './formatCustomListItemSymbol.js'
import camelCase from 'lodash/camelCase.js'
import toLower from 'lodash/toLower.js'
import { getNoteContentAsHtml } from '../../Note/utils/getNoteContentAsHtml.mjs'

const listableNodeTypes = ['noteBoardNote']

function formatOrderedNumber(targetStyle, n) {
  if (targetStyle === 'decimal-leading-zero') {
    return `${n.toString().padStart(2, '0')}`
  }
  if (targetStyle === 'decimal' || targetStyle === 'decimal-multilevel') {
    return `${n}`
  }
  if (targetStyle === 'lower-alpha') {
    return `${toAlpha(n - 1).toLowerCase()}`
  }
  if (targetStyle === 'lower-roman') {
    return `${toRoman(n).toLowerCase()}`
  }
  if (targetStyle === 'upper-alpha') {
    return `${toAlpha(n - 1).toUpperCase()}`
  }
  if (targetStyle === 'upper-roman') {
    return `${toRoman(n).toUpperCase()}`
  }
}

function formatUnorderedBullet(text, scale = 1) {
  return `<span style='display:inline-block; transform:scale(${scale}); transform-origin: center'>${text}</span>`
}

function toAlpha(n) {
  let str = ''
  while (n >= 0) {
    str = String.fromCharCode((n % 26) + 97) + str
    n = Math.floor(n / 26) - 1
  }
  return str
}

export function downgradeCustomExtensionsForDocumentElement(
  documentElement,
  getResource = (resourceType, resourceId) => null,
  options = {
    showMissing: {
      bucketFile: false,
      noteBoardNote: false,
    },
    formatCustomLists: false,
  },
) {
  downgradeCustomExtensionsForElement(documentElement, getResource, options)

  const contiguousLists = []
  let currentList = null

  const itemNoStacks = new Map()

  for (let i = 0; i < documentElement.childNodes.length; i++) {
    const child = documentElement.childNodes[i]
    const tagName = camelCase(toLower(child.tagName))
    if (tagName === 'customList') {
      if (!currentList || currentList.id !== child.getAttribute('list-id')) {
        currentList = {
          index: i,
          items: [],
          id: child.getAttribute('list-id'),
        }
        contiguousLists.push(currentList)
      }
      currentList.items.push(child)
    } else if (listableNodeTypes.includes(tagName) && currentList) {
      currentList.items.push(child)
    } else {
      currentList = null
    }
  }

  if (options?.formatCustomLists) {
    // This converts contiguous <custom-list> elements into a flat set of
    // <ul><li></li></ul> elements, styled to appear as a list.  This is the
    // format that we use for displaying them correctly on the website when
    // being previewed.  This is not the format that we use for the text editor.
    // It's the best way to display them on the website.

    for (let i = contiguousLists.length - 1; i >= 0; i--) {
      const list = contiguousLists[i]
      const listId = list.id
      if (!itemNoStacks.has(listId)) {
        itemNoStacks.set(listId, [])
      }
      const itemNoStack = itemNoStacks.get(listId)

      const resultStacks = []
      for (let j = 0; j < list.items.length; j++) {
        const listItem = list.items[j]
        const depth = parseInt(listItem.getAttribute('depth'))
        while (itemNoStack.length < depth) {
          itemNoStack.push(0)
        }
        while (itemNoStack.length > depth) {
          itemNoStack.pop()
        }
        itemNoStack[depth] = parseInt(listItem.getAttribute('item-number'))
        resultStacks.push([...itemNoStack])
      }
      for (let j = list.items.length - 1; j >= 0; j--) {
        const listItem = list.items[j]
        const depth = parseInt(listItem.getAttribute('depth'))

        const listStyleType = listItem.getAttribute('list-style-type')
        const wrapper = documentElement.ownerDocument.createElement('div')
        wrapper.setAttribute('class', 'custom-list-item-display')
        wrapper.style.display = 'flex'
        wrapper.style.alignItems = 'start'
        const leftCol = documentElement.ownerDocument.createElement('div')
        const rightCol = documentElement.ownerDocument.createElement('div')
        leftCol.setAttribute('class', 'custom-list-item-display-number')
        rightCol.setAttribute('class', 'custom-list-item-display-content')
        leftCol.style.textAlign = 'right'
        leftCol.style.whiteSpace = 'nowrap'
        wrapper.appendChild(leftCol)
        wrapper.appendChild(rightCol)
        const checked = listItem.getAttribute('checked') === 'true'
        const itemNumber = formatCustomListItemSymbol(
          depth,
          resultStacks[j],
          checked,
          listStyleType,
        )
        leftCol.innerHTML = `<p style='padding-right: 0'>${itemNumber}</p>`
        leftCol.style.width = `${(depth + 1) * 48}px`
        rightCol.innerHTML = listItem.innerHTML
        documentElement.insertBefore(wrapper, listItem)
        documentElement.removeChild(listItem)
      }
    }
  } else {
    // This converts contiguous <custom-list> elements into <ul> and <li> elements.
    // It converts them such that they are compatible with Google Docs and this
    // is also the format that we use for the text editor.  It's not the best
    // way to display them, but it pastes well into Google Docs and the editor.

    for (let i = contiguousLists.length - 1; i >= 0; i--) {
      ensureValidListStructure(documentElement, contiguousLists[i])
    }
    // Now that we've validated the lists are legal, turn them into <ul> and <li>
    // elements.

    const processedLists = []
    currentList = null

    for (let i = 0; i < documentElement.childNodes.length; i++) {
      const child = documentElement.childNodes[i]
      const tagName = camelCase(toLower(child.tagName))
      if (tagName === 'customList') {
        if (!currentList || currentList.id !== child.getAttribute('list-id')) {
          currentList = {
            index: i,
            items: [],
            id: child.getAttribute('list-id'),
          }
          processedLists.push(currentList)
        }
        currentList.items.push(child)
      } else if (listableNodeTypes.includes(tagName) && currentList) {
        currentList.items.push(child)
      } else {
        currentList = null
      }
    }
    const htmlLists = []
    for (let i = 0; i < processedLists.length; i++) {
      const listGraph = generateListGraph(processedLists[i], {
        node: documentElement,
        children: [],
      })
      htmlLists.push(generateHtmlListFromGraph(processedLists[i].id, listGraph))
    }
    for (let i = 0; i < htmlLists.length; i++) {
      documentElement.insertBefore(htmlLists[i], processedLists[i].items[0])
      for (let j = 0; j < processedLists[i].items.length; j++) {
        const item = processedLists[i].items[j]
        documentElement.removeChild(item)
      }
    }
  }

  for (let i = 0; i < documentElement.childNodes.length; i++) {
    const child = documentElement.childNodes[i]
    const tagName = camelCase(toLower(child.tagName))
    if (tagName === 'p' && child.innerHTML === '') {
      // Insert a special space element to ensure the paragraph is not
      // collapsed, maintaining the spacing in the document.
      child.innerHTML = '<span class="learnics-inserted-space">&nbsp;</span>'
    }
  }
}

function generateHtmlListFromGraph(listId, graph) {
  const listType = graph.children[0].node.getAttribute('list-style-type')
  const list = graph.node.ownerDocument.createElement(
    (listType === listItemConfigurations[listType].type) === 'ordered'
      ? 'ol'
      : 'ul',
  )
  list.setAttribute('data-custom-list-id', listId)
  for (let i = 0; i < graph.children.length; i++) {
    const child = graph.children[i]
    const liNode = child.node.ownerDocument.createElement('li')
    liNode.setAttribute(
      'data-custom-list-style-type',
      child.node.getAttribute('list-style-type'),
    )
    liNode.innerHTML = child.node?.innerHTML || ''
    for (let j = 0; j < child.listableNodes.length; j++) {
      const cloned = child.listableNodes[j].cloneNode(true)
      liNode.appendChild(cloned)
    }

    if (child.children.length > 0) {
      const subList = generateHtmlListFromGraph(listId, child)
      liNode.appendChild(subList)
    }
    list.appendChild(liNode)
  }
  return list
}

function generateListGraph(
  customList,
  root = {
    node: null,
    children: [],
    listableNodes: [],
  },
) {
  // Create a graph of the list items

  let i = 0
  while (i < customList.items.length) {
    const child = customList.items[i]
    const childDepth = parseInt(child.getAttribute('depth'))

    const listableNodes = []
    while (i + 1 < customList.items.length) {
      const child = customList.items[i + 1]
      const tagName = camelCase(toLower(child.tagName))
      if (listableNodeTypes.includes(tagName)) {
        listableNodes.push(child)
      } else {
        break
      }
      i++
    }
    // Found a child
    let j = i + 1
    while (j < customList.items.length) {
      const nextChild = customList.items[j]
      const nextChildDepth = parseInt(nextChild.getAttribute('depth'))
      if (nextChildDepth <= childDepth) {
        // Found a sibling or parent
        break
      }
      j++
    }
    root.children.push(
      generateListGraph(
        { ...customList, items: [...customList.items.slice(i + 1, j)] },
        {
          node: child,
          children: [],
          listableNodes,
        },
      ),
    )
    i = j
  }

  return root
}

function ensureValidListStructure(
  documentElement,
  customList,
  currentDepth = 0,
) {
  // we need to ensure the custom list has a valid structure to be formatted
  // into an HTML list.  If it doesn't, we need to add in special list elements
  // to ensure the list is valid.

  if (currentDepth !== parseInt(customList.items[0].getAttribute('depth'))) {
    // Insert a new list item at the start of the list with -1 depth
    const newListItem =
      documentElement.ownerDocument.createElement('custom-list')
    newListItem.setAttribute('depth', currentDepth)
    newListItem.setAttribute('list-id', customList.id)
    newListItem.setAttribute(
      'list-style-type',
      customList.items[0].getAttribute('list-style-type'),
    )
    newListItem.setAttribute('pseudo', true)

    documentElement.insertBefore(newListItem, customList.items[0])
    customList.items.unshift(newListItem)
  }
  // Now that we've ensured the bottom level of the list is valid, we need to
  // collect all the items at the current depth and recursively call this
  // function on each of the sublists.

  const subLists = []
  for (let i = 0; i < customList.items.length; i++) {
    const item = customList.items[i]

    const depth = parseInt(item.getAttribute('depth'))
    if (depth === currentDepth) {
      // Found a bottom level item

      let subList = null
      let j = i + 1
      for (; j < customList.items.length; j++) {
        const nextItem = customList.items[j]
        const tagName = camelCase(toLower(nextItem.tagName))
        if (tagName === 'customList') {
          const nextDepth = parseInt(nextItem.getAttribute('depth'))
          if (nextDepth !== depth) {
            // Found an item for a different depth
            subList ||= {
              index: j,
              items: [],
              id: customList.id,
            }
            subList.items.push(nextItem)
          } else {
            break
          }
        }
      }
      if (subList) {
        subLists.push(subList)
      }
      i = j - 1
    }
  }
  for (let i = subLists.length - 1; i >= 0; i--) {
    ensureValidListStructure(documentElement, subLists[i], currentDepth + 1)
  }
}
export function downgradeCustomExtensionsForElement(
  element,
  getResource = (resourceType, resourceId) => null,
  options = {
    showMissing: {
      bucketFile: false,
      noteBoardNote: false,
    },
    formatCustomLists: false,
  },
) {
  const tagName = camelCase(toLower(element.tagName))
  const handler = handlers[tagName]
  if (handler) {
    handler(element, getResource, options)
  } else {
    for (let i = 0; i < element.childNodes.length; i++) {
      downgradeCustomExtensionsForElement(
        element.childNodes[i],
        getResource,
        options,
      )
    }
  }
  return element
}

const handlers = {
  text: (element, getResource, options) => {},
  bucketFile: (element, getResource, options) => {
    const fileId = element.getAttribute('file-id')

    const bucketFile = getResource('bucketFile', fileId)
    if (bucketFile && bucketFile.type.startsWith('image/')) {
      let oldElement = element
      let newElement = oldElement.ownerDocument.createElement('img')
      let scale = oldElement.getAttribute('scale')
      if (scale) {
        scale = parseFloat(scale)
        if (isNaN(scale)) {
          scale = 1
        }
      } else {
        scale = 1
      }

      newElement.setAttribute('data-learnics-resource-type', 'bucketFile')
      newElement.setAttribute('data-learnics-bucket-file-id', fileId)
      newElement.setAttribute('scale', scale)
      newElement.setAttribute('src', bucketFile.signedUrl)
      newElement.setAttribute('alt', bucketFile.name)
      newElement.setAttribute('title', bucketFile.name)
      const naturalWidth = bucketFile.width

      newElement.style.width = `${naturalWidth * scale}px`
      newElement.style.height = `auto`

      const parent = oldElement.parentNode

      parent.insertBefore(newElement, oldElement)
      parent.removeChild(oldElement)
    } else {
      console.warn('Bucket file not found')
      if (options?.showMissing?.bucketFile) {
        element.innerHTML = '<p>[Image]</p>'
      }
    }
  },
  pastedImage: (element, getResource, options) => {
    // If the image got pasted in from somewhere else, it's safe to assume it won't
    // be signed, so we need to replace it with an img tag that is more widely
    // recognized than our custom tag.
    let oldElement = element
    let newElement = oldElement.ownerDocument.createElement('img')
    let scale = oldElement.getAttribute('scale')
    if (scale) {
      scale = parseFloat(scale)
      if (isNaN(scale)) {
        scale = 1
      }
    } else {
      scale = 1
    }

    newElement.setAttribute('scale', scale)
    newElement.setAttribute('src', oldElement.getAttribute('src'))
    const alt = oldElement.getAttribute('alt')
    if (alt) {
      newElement.setAttribute('alt', alt)
    }
    const title = oldElement.getAttribute('title')
    if (title) {
      newElement.setAttribute('title', oldElement.getAttribute('title'))
    }
    const naturalWidth = parseFloat(oldElement.getAttribute('natural-width'))

    newElement.style.width = `${naturalWidth * scale}px`
    newElement.style.height = `auto`

    const parent = oldElement.parentNode

    parent.insertBefore(newElement, oldElement)
    parent.removeChild(oldElement)
  },
  noteBoardNoteContents: (element, getResource, options) => {
    const noteId = element.getAttribute('note-id')

    const note = getResource('note', noteId)
    if (!note) {
      console.warn('Note not found')
      if (options?.showMissing?.noteBoardNote) {
        element.innerHTML = `<p>[Note]</p>`
      }
    } else {
      const containerElement = element.ownerDocument.createElement('div')
      containerElement.innerHTML = getNoteContentAsHtml(note)
      // Recursively downgrade the content of the note in search of bucketFiles, etc
      downgradeCustomExtensionsForDocumentElement(
        containerElement,
        getResource,
        options,
      )
      element.innerHTML = containerElement.innerHTML
    }
  },
}
